import { type FC } from "react";

import { SimpleStockIndicator } from "~/domains/product/components/StockIndicator/StockIndicator";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import StockDataProvider, {
  type StockDataProviderState,
} from "~/features/productDetails/components/PreferredProducts/v1/StockDataProvider";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { trackPdpClick } from "~/app/analytics/ga/ga";
import { type PreferredProductData } from "~/features/productDetails/models/PdpPageProductData";
import Spacer from "~/common/components/Spacer";
import { Link } from "~/common/components/Link";

import { Row } from "~/common/components/Layout";
import useMediaQuery from "~/common/hooks/useMediaQuery";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import ToggleButton from "~/common/components/Links/ToggleButton";
import { MEDIA_QUERY_MIN_DESKTOP_S } from "~/common/utils/screenSizes";
import { useTranslation } from "react-i18next";
import { PREFERRED_PRODUCTS_ID } from "~/features/productDetails/constants";
import { type LoadableEntry } from "~/app/store/loadableTypes";
import { usePurchasePlace } from "~/app/analytics/ga/recommendations/RecommendationContexts";
import { appendPurchaseParams } from "~/features/productDetails/components/ProductSummary/PdpSummaryHelper";
import StarIcon from "@mui/icons-material/Star";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { styled } from "~/common/mui";

const MAX_VISIBLE_PRODUCTS = 5;

interface PreferredProductsBlockProps {
  productFamilyName: string;
  preferredProducts: LoadableEntry<{
    preferred: PreferredProductData[];
  }>;
}

export const PreferredProductsBlock: FC<PreferredProductsBlockProps> = (props) => {
  const { t } = useTranslation();
  const isDesktop = useMediaQuery(MEDIA_QUERY_MIN_DESKTOP_S);
  const { val: showAll, toggle: toggleShowAll } = useBooleanState(false);

  const preferredProducts = props.preferredProducts.data?.preferred ?? [];

  if (preferredProducts.length === 0) {
    return null;
  }

  const isShowMoreVisible = preferredProducts.length > MAX_VISIBLE_PRODUCTS;

  const showStock = userHasPermission(Permission.AVAILABILITY);

  const { productFamilyName } = props;

  const title = t("pdp.preferredProducts.title", { code: productFamilyName });

  const renderContent = ({ isLoading, isError, inStock }: StockDataProviderState) => {
    const getIsInStock = (product: PreferredProductData) => {
      if (!showStock || isLoading) {
        return undefined;
      }
      if (isError) {
        return false;
      }
      return inStock[product.code];
    };

    const visibleElements = showAll ? preferredProducts : preferredProducts.slice(0, MAX_VISIBLE_PRODUCTS);

    return (
      <PreferredProductsContainer className={"scha__preferred-products__container"}>
        {visibleElements.map((product) => (
          <PreferredProductsItem className="scha__preferred-products__item col-12 col-xs-6 col-sm-4 col-md-2" key={product.code}>
            <PreferredProduct product={product} inStock={getIsInStock(product)} showStock={showStock} />
          </PreferredProductsItem>
        ))}
      </PreferredProductsContainer>
    );
  };

  let content;
  if (showStock) {
    content = <StockDataProvider products={preferredProducts}>{renderContent}</StockDataProvider>;
  } else {
    content = renderContent({ isLoading: false, isError: false, inStock: {} });
  }

  return (
    <PreferredProductsRoot id={PREFERRED_PRODUCTS_ID} className="scha__preferred-products">
      {isDesktop && <Spacer className={"mb-30"} />}
      <PreferredProductsTitle className={"scha__preferred-products__title"}>
        <PreferredProductsTitleIcon className={"scha__preferred-products__title__icon"}  />
        {title}
      </PreferredProductsTitle>
      {content}
      {isShowMoreVisible && <ToggleButton className={"mt-3"} isOpen={showAll} onToggle={toggleShowAll} />}
    </PreferredProductsRoot>
  );
};
export default PreferredProductsBlock;

interface PreferredProductProps {
  product: PreferredProductData;
  showStock: boolean;
  inStock?: boolean;
}

const PreferredProduct: FC<PreferredProductProps> = ({ product, inStock, showStock }) => {
  const reco_place = usePurchasePlace();
  const trackClick = () => trackPdpClick("Preferred Product", product.designation);

  const href = appendPurchaseParams(product.url, reco_place, "pp");

  return (
    <div className={"scha__preferred_product"}>
      <Link
        href={href}
        onClick={trackClick}
        className={"label-large"}
        iconBefore={<KeyboardArrowRightIcon  />}
      >
        {product.designation}
      </Link>
      {showStock && (
        <PreferredProductStockContainer className={"scha__preferred_product__stock-container"}>
          {inStock === undefined ? <CircleSpinner /> : <SimpleStockIndicator inStock={inStock} />}
        </PreferredProductStockContainer>
      )}
    </div>
  );
};

const PreferredProductsRoot = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(4),
}));

const PreferredProductsTitle = styled("h3")(({ theme }) => ({
  marginTop: theme.spacing(3.75),
  marginBottom: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  ...theme.typography.titleLarge,
  [theme.breakpoints.up("desktop-s")]: {
    marginBottom: theme.spacing(3.75),
    marginTop: 0,
  },
}));

const PreferredProductsTitleIcon = styled(StarIcon)(({ theme }) => ({
  color: theme.palette.primary.main,
  marginRight: theme.spacing(1),
}));

const PreferredProductsContainer = styled(Row)(({ theme }) => ({
  margin: theme.spacing(-1),
  [theme.breakpoints.up("tablet")]: {
    margin: theme.spacing(-1.5),
  },
  [theme.breakpoints.up("desktop-s")]: {
    margin: theme.spacing(-2),
  },
}));

const PreferredProductsItem = styled("div")(({ theme }) => ({
  padding: theme.spacing(1),
  [theme.breakpoints.up("tablet")]: {
    padding: theme.spacing(1.5),
  },
  [theme.breakpoints.up("desktop-s")]: {
    padding: theme.spacing(2),
  },
}));

const PreferredProductStockContainer = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(0.625),
  paddingLeft: theme.spacing(2.875),
}));
